"use client";

import { useTranslation } from 'react-i18next';
import React from 'react';

type ResumeDecisionMode = 'resume' | 'askLogin';

interface ResumeDecisionContentProps {
    slug: string;
    applicationId: string;
    surveyName?: string;
    userId?: string; // User ID (guest or member)
    isCompleted?: boolean; // True if survey was completed (show different message)
    onContinue: () => void;
    onRestart: () => void;
    // askLogin mode (shown to guests/anonymous on a fresh start — see category-page-wrapper)
    mode?: ResumeDecisionMode;
    onSignUp?: () => void;
    onContinueAsGuest?: () => void;
}

/**
 * Resume decision content that fits inside survey layout
 * Matches the style of category intro/question screens
 */
export default function ResumeDecisionContent({
    slug,
    applicationId,
    surveyName = 'แบบสอบถาม',
    userId,
    isCompleted = false,
    onContinue,
    onRestart,
    mode = 'resume',
    onSignUp,
    onContinueAsGuest,
}: ResumeDecisionContentProps) {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = React.useState(false);

    // Safety net: isLoading is set true on click and only cleared on error —
    // callers assume this component unmounts right after a successful action
    // (sign-up navigates away; restart/continue normally swap this screen out).
    // If the parent instead keeps the SAME instance mounted and just changes
    // `mode`/`isCompleted` to show a different decision, that stale isLoading
    // would otherwise leak in and disable buttons the user never touched. The
    // call site also keys this component by mode so it remounts fresh across
    // mode changes — this effect covers the remaining case where mode is
    // unchanged but the screen's content (isCompleted) changes.
    React.useEffect(() => {
        setIsLoading(false);
    }, [mode, isCompleted]);

    const handleContinue = async () => {
        setIsLoading(true);
        try {
            await onContinue();
        } catch (error) {
            console.error('Error in handleContinue:', error);
            setIsLoading(false);
        }
    };

    const handleStartFresh = async () => {
        setIsLoading(true);

        try {

            // Simply call the parent's restart handler
            // All clearing logic is handled in category-page-wrapper.tsx
            await onRestart();
        } catch (error) {
            console.error('❌ [ResumeDecision] Error during restart:', error);
            setIsLoading(false);
        }
    };

    const handleSignUpClick = () => {
        if (!onSignUp) return;
        setIsLoading(true);
        try {
            onSignUp();
        } catch (error) {
            console.error('❌ [ResumeDecision] Error in onSignUp:', error);
            setIsLoading(false);
        }
    };

    const handleGuestClick = () => {
        if (!onContinueAsGuest) return;
        try {
            onContinueAsGuest();
        } catch (error) {
            console.error('❌ [ResumeDecision] Error in onContinueAsGuest:', error);
        }
    };

    if (mode === 'askLogin') {
        return (
            <div className="w-full h-full flex items-center justify-center px-4">
                <div className="max-w-xl w-full text-center">
                    {/* Title */}
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4 leading-relaxed">
                        {t('survey_form.resume_decision.ask_login_title')}
                    </h2>

                    {/* Description */}
                    <p className="text-base md:text-lg text-gray-600 mb-6 leading-relaxed">
                        {t('survey_form.resume_decision.ask_login_description')}
                    </p>

                    {/* Guest warning */}
                    {/* <div className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-lg text-left">
                        <div className="flex items-start gap-2">
                            <span className="text-amber-600 mt-0.5 flex-shrink-0">⚠️</span>
                            <p className="text-sm text-amber-800 leading-relaxed">
                                {t('survey_form.resume_decision.ask_login_guest_warning')}
                            </p>
                        </div>
                    </div> */}

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                        {/* Continue as guest — outline */}
                        <button
                            onClick={handleGuestClick}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !isLoading) {
                                    e.preventDefault();
                                    handleGuestClick();
                                }
                            }}
                            disabled={isLoading}
                            className="w-full sm:w-auto px-8 py-4 bg-white border-2 border-gray-300 text-gray-700 font-semibold rounded-full hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed min-w-[200px]"
                            style={{ fontSize: '1rem' }}
                        >
                            {t('survey_form.resume_decision.ask_login_btn_guest')}
                        </button>

                        {/* Sign in / Sign up — primary */}
                        <button
                            onClick={handleSignUpClick}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !isLoading) {
                                    e.preventDefault();
                                    handleSignUpClick();
                                }
                            }}
                            disabled={isLoading}
                            className="w-full sm:w-auto px-8 py-4 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-full transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed min-w-[200px] shadow-md hover:shadow-lg"
                            style={{ fontSize: '1rem' }}
                        >
                            {t('survey_form.resume_decision.ask_login_btn_signin')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Default: resume mode (existing behavior)
    return (
        <div className="w-full h-full flex items-center justify-center px-4">
            <div className="max-w-xl w-full text-center">
                {/* Main Question */}
                <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-8 leading-relaxed">
                    {isCompleted ? (
                        <>
                            {t('survey_form.resume_decision.completed_title', 'คุณต้องการแก้ไขคำตอบ')}<br />
                            {t('survey_form.resume_decision.completed_subtitle', 'หรือเริ่มทำใหม่หรือไม่')}
                        </>
                    ) : (
                        <>
                            {t('survey_form.resume_decision.title')}<br />
                            {t('survey_form.resume_decision.subtitle')}
                        </>
                    )}
                </h2>

                {/* Action Buttons - ตรงตามรูป */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    {/* ปุ่มเริ่มใหม่ - ขาว outline */}
                    <button
                        onClick={handleStartFresh}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !isLoading) {
                                e.preventDefault();
                                handleStartFresh();
                            }
                        }}
                        disabled={isLoading}
                        className="w-full sm:w-auto px-12 py-4 bg-white border-2 border-gray-300 text-gray-700 font-semibold rounded-full hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed min-w-[160px]"
                        style={{ fontSize: '1.125rem' }}
                    >
                        {isLoading ? t('survey_form.resume_decision.clearing') : t('survey_form.resume_decision.btn_restart')}
                    </button>

                    {/* ปุ่มทำต่อ/แก้ไข - เขียว solid */}
                    <button
                        onClick={handleContinue}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !isLoading) {
                                e.preventDefault();
                                handleContinue();
                            }
                        }}
                        disabled={isLoading}
                        className="w-full sm:w-auto px-12 py-4 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-full transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed min-w-[160px] shadow-md hover:shadow-lg"
                        style={{ fontSize: '1.125rem' }}
                    >
                        {isCompleted
                            ? t('survey_form.resume_decision.btn_edit', 'แก้ไขคำตอบ')
                            : t('survey_form.resume_decision.btn_continue')}
                    </button>
                </div>
            </div>
        </div>
    );
}
